import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  MenuItem,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material';
import { Close, CloudUpload, Delete as DeleteIcon } from '@mui/icons-material';
import {
  KnowledgeBaseLink,
  KnowledgeBaseLinkFormData,
  KnowledgeBaseLinkFormErrors,
} from '@/constants/types/knowledgeBase.types';
import { Folder } from '@/constants/types/folder.types';

interface KnowledgeBaseDialogProps {
  open: boolean;
  link: KnowledgeBaseLink | null;
  folders: Folder[];
  onClose: () => void;
  onSubmit: (data: KnowledgeBaseLinkFormData) => void;
  isLoading: boolean;
  isUploading: boolean;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

export const KnowledgeBaseDialog: React.FC<KnowledgeBaseDialogProps> = ({
  open,
  link,
  folders,
  onClose,
  onSubmit,
  isLoading,
  isUploading,
}) => {
  const [formData, setFormData] = useState<KnowledgeBaseLinkFormData>({
    folderId: '',
    title: '',
    description: '',
    document: null,
  });
  const [errors, setErrors] = useState<KnowledgeBaseLinkFormErrors>({});

  useEffect(() => {
    if (open) {
      if (link) {
        // Extract folderId - handle both string and populated object
        const folderId = typeof link.folderId === 'string'
          ? link.folderId
          : link.folderId._id;

        setFormData({
          folderId,
          title: link.title,
          description: link.description,
          document: null,
        });
      } else {
        setFormData({
          folderId: '',
          title: '',
          description: '',
          document: null,
        });
      }
      setErrors({});
    }
  }, [open, link]);

  const validateForm = (): boolean => {
    const newErrors: KnowledgeBaseLinkFormErrors = {};

    if (!formData.folderId) {
      newErrors.folderId = 'Folder is required';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be less than 200 characters';
    }

    if (!formData.document && !link) {
      newErrors.document = 'Document is required';
    }

    if (formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    if (formData.document) {
      if (formData.document.size > MAX_FILE_SIZE) {
        newErrors.document = 'File size must be less than 5MB';
      }
      if (!ALLOWED_TYPES.includes(formData.document.type)) {
        newErrors.document = 'Only PDF and DOC files are allowed';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange =
    (field: keyof KnowledgeBaseLinkFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({
      ...prev,
      document: file,
    }));
    if (errors.document) {
      setErrors((prev) => ({
        ...prev,
        document: undefined,
      }));
    }
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      document: null,
    }));
  };

  const isEditMode = !!link;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" component="span">
          {isEditMode ? 'Edit Knowledge Base Link' : 'Add to Knowledge Base'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isLoading || isUploading}
          sx={{ color: 'grey.500' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Uploading document...
            </Alert>
            <LinearProgress />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            select
            label="Folder"
            value={formData.folderId}
            onChange={handleChange('folderId')}
            error={!!errors.folderId}
            helperText={errors.folderId || 'Select the folder for this resource'}
            disabled={isLoading || isUploading}
            required
            fullWidth
          >
            {folders.map((folder) => (
              <MenuItem key={folder._id} value={folder._id}>
                {'  '.repeat(folder.level - 1)}
                {folder.name} (Level {folder.level})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Title"
            value={formData.title}
            onChange={handleChange('title')}
            error={!!errors.title}
            helperText={errors.title || `${formData.title.length}/200 characters`}
            disabled={isLoading || isUploading}
            autoFocus
            fullWidth
            required
          />


          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Upload Document (PDF or DOC, max 5MB)
            </Typography>
            <input
              accept=".pdf,.doc,.docx"
              style={{ display: 'none' }}
              id="document-upload"
              type="file"
              onChange={handleFileChange}
              disabled={isLoading || isUploading}
            />
            <label htmlFor="document-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                disabled={isLoading || isUploading}
                fullWidth
              >
                {formData.document ? 'Change Document' : 'Upload Document'}
              </Button>
            </label>
            {formData.document && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`${formData.document.name} (${(formData.document.size / 1024).toFixed(2)} KB)`}
                  onDelete={handleRemoveFile}
                  deleteIcon={<DeleteIcon />}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
            {errors.document && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors.document}
              </Typography>
            )}
          </Box>

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            error={!!errors.description}
            helperText={
              errors.description || `${formData.description.length}/1000 characters`
            }
            disabled={isLoading || isUploading}
            fullWidth
            multiline
            rows={4}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={isLoading || isUploading}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || isUploading}
          variant="contained"
          color="primary"
        >
          {isLoading || isUploading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
